import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Loader2, AlertCircle, Lock, Mail, User, Phone, Eye, EyeOff, ArrowRight, KeyRound, UserPlus, Shield, Zap, Smartphone, Scale, RefreshCw, BarChart3, Users, Headphones, FileText, Clock, CheckCircle, Star, Heart, Globe, Key, Wallet, TrendingUp, Award, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast, Toaster } from 'sonner';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';

// Import the actual ClientLayout for consistent look and feel
import ClientLayout from '../../components/client/ClientLayout';
import BrandMark from '../../components/BrandMark';
import PartnerMark from '../../components/PartnerMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping — mirrors ClientLogin so the admin-configured feature icons render identically here.
const ICON_MAP = {
  Shield, Lock, Zap, Smartphone, Scale, RefreshCw, BarChart3, Users, Headphones,
  FileText, Clock, CheckCircle, Star, Heart, Globe, Key, Wallet, TrendingUp, Award, Briefcase,
};

// Default 9 "What You'll Get" cards (same as ClientLogin defaults).
const DEFAULT_LOGIN_FEATURES = [
  { id: '1', icon: 'Shield', title: 'Secure Vault', description: 'Store all your important documents in one encrypted, protected space', enabled: true, order: 1 },
  { id: '2', icon: 'Zap', title: 'Quick Setup', description: 'Create your will in minutes with our guided step-by-step process', enabled: true, order: 2 },
  { id: '3', icon: 'Smartphone', title: 'Access Anywhere', description: 'View and manage your documents from any device, anytime', enabled: true, order: 3 },
  { id: '4', icon: 'Scale', title: 'Legally Valid', description: 'Documents crafted to meet all legal requirements and standards', enabled: true, order: 4 },
  { id: '5', icon: 'RefreshCw', title: 'Easy Updates', description: 'Modify your will anytime as your life circumstances change', enabled: true, order: 5 },
  { id: '6', icon: 'BarChart3', title: 'Track Progress', description: 'Monitor your will completion status with clear visual indicators', enabled: true, order: 6 },
  { id: '7', icon: 'Users', title: 'Trusted Contacts', description: 'Share access with family members for emergency situations', enabled: true, order: 7 },
  { id: '8', icon: 'Lock', title: 'Bank-Grade Security', description: 'Enterprise-level encryption protects your sensitive data', enabled: true, order: 8 },
  { id: '9', icon: 'Headphones', title: 'Expert Support', description: 'Get help from our team whenever you need assistance', enabled: true, order: 9 },
];

// Embedded Login Component - Same flow as ClientLogin
const EmbedAuth = ({ partnerId, partnerInfo, onAuthSuccess }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'set_password' | 'register'
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [clientData, setClientData] = useState(null);
  // Admin-configured "What You'll Get" 9 cards (matches /c/<subdomain>/login).
  const [loginFeatures, setLoginFeatures] = useState({
    enabled: true,
    section_title: "What You'll Get",
    cards: DEFAULT_LOGIN_FEATURES,
  });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await axios.get(`${API}/login-features`);
        if (!active) return;
        if (resp.data) {
          setLoginFeatures({
            enabled: resp.data.enabled !== false,
            section_title: resp.data.section_title || "What You'll Get",
            cards: Array.isArray(resp.data.cards) && resp.data.cards.length ? resp.data.cards : DEFAULT_LOGIN_FEATURES,
          });
        }
      } catch (e) {
        // swallow — defaults apply
      }
    })();
    return () => { active = false; };
  }, []);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });

  // Check email and determine next step
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/client/login`, {
        email: formData.email,
        subdomain: partnerInfo?.subdomain,
      });

      if (response.data.requires_password_setup) {
        // First-time login - user needs to set a password
        setClientData({ name: response.data.client?.name || response.data.client_name, email: formData.email });
        setStep('set_password');
        toast.info('Welcome! Please set your password to continue.');
      } else if (response.data.requires_password) {
        // Existing user with password - show password form
        setClientData({ name: response.data.client_name, email: formData.email });
        setStep('password');
      } else if (response.data.requires_registration) {
        // User doesn't exist - show registration form
        setStep('register');
        toast.info('Create a new account to get started.');
      } else {
        // Unexpected response - default to password step
        setClientData({ name: response.data.client_name || '', email: formData.email });
        setStep('password');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setStep('register');
        toast.info('Create a new account to get started.');
      } else {
        // Handle FastAPI validation errors (422) which return an array
        let message = 'An error occurred. Please try again.';
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          message = detail[0]?.msg || message;
        }
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password login
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/client/login`, {
        email: formData.email,
        password: formData.password,
        subdomain: partnerInfo?.subdomain,
      });

      if (response.data.token) {
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientEmail', response.data.client.email);
        localStorage.setItem('clientSubdomain', partnerInfo?.subdomain);
        localStorage.setItem('isEmbedded', 'true');
        
        toast.success('Login successful!');
        onAuthSuccess(response.data);
      }
    } catch (err) {
      let message = 'Invalid credentials. Please try again.';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0]?.msg || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle first-time password setup
  const handleSetPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/client/set-password`, {
        email: formData.email,
        password: formData.password,
        subdomain: partnerInfo?.subdomain,
      });

      if (response.data.token) {
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientEmail', response.data.client.email);
        localStorage.setItem('clientSubdomain', partnerInfo?.subdomain);
        localStorage.setItem('isEmbedded', 'true');
        
        toast.success('Password set successfully!');
        onAuthSuccess(response.data);
      }
    } catch (err) {
      let message = 'Failed to set password. Please try again.';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0]?.msg || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new user registration
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.mobile && !/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not ready. Please try again.');
      return;
    }

    setIsLoading(true);

    try {
      // Get reCAPTCHA v3 token (mandatory after security audit C-5)
      const recaptchaToken = await executeRecaptcha('client_register');

      const response = await axios.post(`${API}/client/register`, {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        subdomain: partnerInfo?.subdomain,
        recaptcha_token: recaptchaToken,
      });

      if (response.data.token) {
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientEmail', response.data.client.email);
        localStorage.setItem('clientSubdomain', partnerInfo?.subdomain);
        localStorage.setItem('isEmbedded', 'true');
        
        toast.success('Account created successfully!');
        onAuthSuccess(response.data);
      }
    } catch (err) {
      let message = 'Registration failed. Please try again.';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0]?.msg || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!formData.email) {
      toast.error('Please enter your email first');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/client/forgot-password`, {
        email: formData.email,
        subdomain: partnerInfo?.subdomain,
      });
      toast.success('If an account exists with this email, you will receive a password reset link shortly.');
      setStep('email');
    } catch (err) {
      toast.error('Failed to process request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setStep('email');
    setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
        {/* Left — login card */}
        <div className="lg:col-span-5">
          <Card className="bg-slate-900/80 border-slate-800">
        <CardHeader className="text-center pb-4">
          {partnerInfo?.logo ? (
            <img
              src={partnerInfo.logo.startsWith('http') ? partnerInfo.logo : `${BACKEND_URL}${partnerInfo.logo}`}
              alt={partnerInfo.partner_name}
              className="w-16 h-16 mx-auto mb-4 rounded-xl object-contain bg-slate-800"
            />
          ) : (
            <div className="flex justify-center mb-4">
              <PartnerMark partnerInfo={partnerInfo} size="xl" showName={false} />
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-white">
            {partnerInfo?.partner_name || 'Client Portal'}
          </CardTitle>
          <p className="text-slate-400 text-sm mt-1">Secure Will Creation Platform</p>
        </CardHeader>
        
        <CardContent>
          {/* Email Step */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-5" data-testid="embed-email-form">
              <div>
                <Label className="text-slate-300 mb-2 block">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="email"
                    required
                    autoFocus
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
                    placeholder="Enter your email"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                    data-testid="embed-email-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="embed-continue-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Password Step */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5" data-testid="embed-password-form">
              <div className="text-center mb-4">
                <p className="text-slate-400">Welcome back,</p>
                <p className="text-white font-medium">{clientData?.name || formData.email}</p>
              </div>
              
              <div>
                <Label className="text-slate-300 mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoFocus
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                    data-testid="embed-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="embed-login-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Login'}
              </Button>

              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={goBack}
                  className="text-slate-400 hover:text-white"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-amber-400 hover:text-amber-300"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {/* Set Password Step (First Time) */}
          {step === 'set_password' && (
            <form onSubmit={handleSetPasswordSubmit} className="space-y-5" data-testid="embed-setpassword-form">
              <div className="text-center mb-4">
                <KeyRound className="w-12 h-12 text-amber-400 mx-auto mb-2" />
                <p className="text-white font-medium">Set Your Password</p>
                <p className="text-slate-400 text-sm">{formData.email}</p>
              </div>
              
              <div>
                <Label className="text-slate-300 mb-2 block">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    autoFocus
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create password (min 8 characters)"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Set Password & Continue'}
              </Button>

              <button
                type="button"
                onClick={goBack}
                className="w-full text-slate-400 hover:text-white text-sm"
              >
                ← Back
              </button>
            </form>
          )}

          {/* Register Step */}
          {step === 'register' && (
            <form onSubmit={handleRegisterSubmit} className="space-y-4" data-testid="embed-register-form">
              <div className="text-center mb-4">
                <UserPlus className="w-12 h-12 text-amber-400 mx-auto mb-2" />
                <p className="text-white font-medium">Create Account</p>
                <p className="text-slate-400 text-sm">{formData.email}</p>
              </div>
              
              <div>
                <Label className="text-slate-300 mb-2 block">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="text"
                    required
                    autoFocus
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block">Mobile (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="Enter mobile number"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create password (min 8 characters)"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
              </Button>

              <button
                type="button"
                onClick={goBack}
                className="w-full text-slate-400 hover:text-white text-sm"
              >
                ← Back
              </button>
            </form>
          )}

          {/* Powered by footer */}
          <div className="mt-6 pt-4 border-t border-slate-800 text-center">
            <p className="text-slate-500 text-xs">
              Powered by <span className="text-amber-400">WillKart.ai</span>
            </p>
          </div>
        </CardContent>
      </Card>
        </div>

        {/* Right — What You'll Get feature grid (desktop). Stacks below on mobile. */}
        {loginFeatures.enabled && loginFeatures.cards?.some(c => c.enabled) && (
          <section className="lg:col-span-7" data-testid="embed-features-section">
            <div className="mb-5 lg:mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {loginFeatures.section_title || "What You'll Get"}
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {loginFeatures.cards
                .filter(card => card.enabled)
                .sort((a, b) => a.order - b.order)
                .map((card) => {
                  const IconComponent = ICON_MAP[card.icon] || Shield;
                  return (
                    <div
                      key={card.id}
                      className="group bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 hover:bg-slate-800/50 hover:border-slate-600/50 transition-all duration-300"
                      data-testid={`embed-feature-card-${card.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                          <IconComponent className="w-5 h-5 text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-sm mb-1">
                            {card.title}
                          </h3>
                          <p className="text-slate-400 text-xs leading-relaxed">
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// Main Embed Portal Component
const EmbedClientPortal = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    validateAndInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const validateAndInitialize = async () => {
    try {
      // Validate embed access
      const response = await axios.get(`${API}/embed/${partnerId}/validate`);
      
      if (!response.data.valid) {
        setError(response.data.reason || 'Access denied');
        setLoading(false);
        return;
      }

      setPartnerInfo(response.data);

      // Check if user is already logged in
      const token = localStorage.getItem('clientToken');
      const storedSubdomain = localStorage.getItem('clientSubdomain');
      
      if (token && storedSubdomain === response.data.subdomain) {
        // Verify token is still valid
        try {
          await axios.get(`${API}/client/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setIsAuthenticated(true);
          
          // Navigate to dashboard if at root
          if (location.pathname === `/embed/${partnerId}` || 
              location.pathname === `/embed/${partnerId}/` ||
              location.pathname === `/embed/${partnerId}/client`) {
            navigate(`/embed/${partnerId}/dashboard`, { replace: true });
          }
        } catch (e) {
          // Token invalid, clear and show login
          localStorage.removeItem('clientToken');
          localStorage.removeItem('clientId');
          localStorage.removeItem('clientName');
          localStorage.removeItem('clientEmail');
          localStorage.removeItem('clientSubdomain');
          localStorage.removeItem('isEmbedded');
        }
      }
    } catch (error) {
      console.error('Embed validation error:', error);
      setError(error.response?.data?.detail || 'Failed to load portal');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    navigate(`/embed/${partnerId}/dashboard`, { replace: true });
  };

  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientName');
    localStorage.removeItem('clientEmail');
    localStorage.removeItem('clientSubdomain');
    localStorage.removeItem('isEmbedded');
    setIsAuthenticated(false);
    navigate(`/embed/${partnerId}/client`, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading portal...</p>
        </div>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-900/80 border-red-500/30">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return (
      <>
        <EmbedAuth
          partnerId={partnerId}
          partnerInfo={partnerInfo}
          onAuthSuccess={handleAuthSuccess}
        />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Authenticated - render ClientLayout which wraps the Outlet for nested routes
  // We pass the outlet with context so child routes can access embed info via useOutletContext()
  const outletContext = { 
    partnerInfo, 
    subdomain: partnerInfo?.subdomain, 
    useSubdomainRouting: false,
    isEmbedded: true,
    embedPartnerId: partnerId
  };

  return (
    <ClientLayout 
      isEmbedded={true} 
      embedPartnerId={partnerId}
      onLogout={handleLogout}
    >
      <Outlet context={outletContext} />
    </ClientLayout>
  );
};

export default EmbedClientPortal;
